import { supabase, waitForSupabaseReady } from './supabase'

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

function getSessionId(): string {
  let id = sessionStorage.getItem('miac_session_id')
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem('miac_session_id', id)
  }
  return id
}

// ---------------------------------------------------------------------------
// User ID cache
// ---------------------------------------------------------------------------

let cachedUserId: string | null = null

async function getUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId
  try {
    const { data: { user } } = await supabase.auth.getUser()
    cachedUserId = user?.id ?? null
  } catch {
    cachedUserId = null
  }
  return cachedUserId
}

// Listen for auth changes to keep cachedUserId fresh
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null
})

// ---------------------------------------------------------------------------
// Event buffer
// ---------------------------------------------------------------------------

interface AnalyticsEvent {
  user_id: string | null
  session_id: string
  event_name: string
  event_category: string
  properties: Record<string, unknown>
  flow_name: string | null
  flow_step: string | null
  flow_step_index: number | null
  created_at: string
}

const BUFFER_LIMIT = 20
const FLUSH_INTERVAL_MS = 30_000

let buffer: AnalyticsEvent[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let flushing = false

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Track a discrete BI event. This is fire-and-forget; it never blocks the
 * caller or triggers React re-renders.
 */
export function track(
  eventName: string,
  properties: Record<string, unknown> = {},
  category = 'bi',
): void {
  pushEvent({
    event_name: eventName,
    event_category: category,
    properties,
    flow_name: null,
    flow_step: null,
    flow_step_index: null,
  })
}

/**
 * Track a step in a multi-step funnel.
 */
export function trackFunnel(
  flowName: string,
  step: string,
  stepIndex: number,
  properties: Record<string, unknown> = {},
): void {
  pushEvent({
    event_name: `${flowName}:${step}`,
    event_category: 'funnel',
    properties,
    flow_name: flowName,
    flow_step: step,
    flow_step_index: stepIndex,
  })
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function pushEvent(
  partial: Omit<AnalyticsEvent, 'user_id' | 'session_id' | 'created_at'>,
): void {
  const event: AnalyticsEvent = {
    ...partial,
    user_id: cachedUserId,
    session_id: getSessionId(),
    created_at: new Date().toISOString(),
  }

  buffer.push(event)

  if (buffer.length >= BUFFER_LIMIT) {
    scheduleFlush()
  }
}

function scheduleFlush(): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => void flushEvents())
  } else {
    setTimeout(() => void flushEvents(), 0)
  }
}

async function flushEvents(): Promise<void> {
  if (flushing || buffer.length === 0) return
  flushing = true

  const batch = buffer.splice(0)

  try {
    await waitForSupabaseReady()

    // Back-fill user_id for events that were buffered before auth was ready
    if (cachedUserId) {
      for (const evt of batch) {
        if (!evt.user_id) evt.user_id = cachedUserId
      }
    } else {
      const uid = await getUserId()
      if (uid) {
        for (const evt of batch) {
          if (!evt.user_id) evt.user_id = uid
        }
      }
    }

    const { error } = await supabase.from('analytics_events').insert(batch)
    if (error) {
      // Put events back so they aren't lost
      buffer.unshift(...batch)
    }
  } catch {
    buffer.unshift(...batch)
  } finally {
    flushing = false
  }
}

// ---------------------------------------------------------------------------
// Periodic flush
// ---------------------------------------------------------------------------

function startPeriodicFlush(): void {
  if (flushTimer) return
  flushTimer = setInterval(() => {
    scheduleFlush()
  }, FLUSH_INTERVAL_MS)
}

// ---------------------------------------------------------------------------
// Page unload – use sendBeacon so events survive navigation
// ---------------------------------------------------------------------------

function handleBeforeUnload(): void {
  if (buffer.length === 0) return

  // Back-fill user_id one last time
  if (cachedUserId) {
    for (const evt of buffer) {
      if (!evt.user_id) evt.user_id = cachedUserId
    }
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!supabaseUrl || !supabaseKey) return

  const url = `${supabaseUrl}/functions/v1/analytics-ingest`
  const payload = JSON.stringify({ events: buffer.splice(0) })

  try {
    navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }))
  } catch {
    // Last resort — ignore if sendBeacon is unavailable
  }
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

function init(): void {
  startPeriodicFlush()
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload()
      }
    })
  }
}

init()
